import { Dispatch, SetStateAction } from 'react';

import { Card } from '@components/card';
import { useFormContext } from '@components/form';
import { FormInstance } from '@components/form/create-form';
import { IconButton } from '@components/icon-button';
import { IconTrash } from '@components/icons/trash';
import { hideExtendedSidebar, useExtendedSidebarData } from '@components/sidebar';
import { Stack } from '@components/stack';
import { FieldMappingGroup, VariableValue } from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { ComparatorComponent } from './comparator-component';
import { filterSelectAttributeVariables } from './field-utils';

export type Condition = {
  id: string;
  field?: VariableValue;
  comparator?: string;
  value?: any;
};

export const getComparatorIds = (id: string) => ({
  elementId: `${id}Element`,
  comparatorId: `${id}Comparator`,
  valueId: `${id}Value`,
});

type ConditionComponentProps<T> = {
  variableGroups: FieldMappingGroup[];
  setConditions: Dispatch<SetStateAction<Condition[]>>;
  condition: Condition;
  onRemove: () => void;
  form: FormInstance<T>;
  isWebhook?: boolean;
  index?: number;
  fieldMappingId: string;
};

export const ConditionComponent = <T extends Record<string, any>>({
  variableGroups,
  setConditions,
  condition,
  onRemove,
  form: Form,
  isWebhook,
  index,
  fieldMappingId,
}: ConditionComponentProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const { elementId } = getComparatorIds(condition.id);
  const { isExtended } = useExtendedSidebarData();

  const { store } = useFormContext();
  const element = store.useFieldValue(elementId) as VariableValue;

  const travellerVariableGroup = variableGroups.find((group) => group.id === 'traveller');

  if (travellerVariableGroup) {
    travellerVariableGroup.fields = filterSelectAttributeVariables(travellerVariableGroup.fields);
  }

  const handleChangeFieldMapping = () => {
    setConditions((conditions: Condition[]) =>
      conditions.map((c: Condition) => {
        return c.id === condition.id
          ? {
              ...c,
              comparator: undefined,
              value: undefined,
              field: { value: '' },
            }
          : c;
      }),
    );
  };

  const handleRemove = () => {
    if (isExtended) {
      hideExtendedSidebar();
    }
    onRemove();
  };

  return (
    <>
      <Card space={2}>
        <Stack space={2}>
          <Stack space={2} orientation="horizontal">
            {/* width: 0 rather than minWidth: 0 — both let the row shrink, but minWidth: 0 leaves
                the card's left edge unpainted on the first open in Chrome on Retina screens */}
            <div style={{ flexGrow: 1, width: 0 }}>
              <Form.FieldMappingField
                id={elementId as any}
                label={t('elementLabel')}
                variableGroups={variableGroups}
                defaultValue={condition.field}
                validators={['required']}
                singleLine
                plaintext
                onChange={handleChangeFieldMapping}
                fieldMappingId={fieldMappingId}
              />
            </div>
            <IconButton
              icon={<IconTrash />}
              variant="secondary-purple"
              onClick={handleRemove}
              style={{ marginTop: pxToRem(28) }}
            />
          </Stack>
          {element?.value && (
            <ComparatorComponent
              key={JSON.stringify(element)}
              form={Form}
              condition={{
                id: condition.id,
                field: condition.field ?? { value: '' },
                comparator: condition.comparator,
                value: condition.value,
              }}
              variableGroups={variableGroups}
              fieldMappingId={fieldMappingId}
              isWebhook={isWebhook}
              index={index}
              elementIdKey={elementId as keyof T}
              comparatorIdKey={getComparatorIds(condition.id).comparatorId as keyof T}
              valueIdKey={getComparatorIds(condition.id).valueId as keyof T}
            />
          )}
        </Stack>
      </Card>
    </>
  );
};

const locale = {
  en: {
    elementLabel: 'Element',
  },
};
